import React, { useEffect, useState } from 'react';
import Icon from '../../../../components/AppIcon';

const PasswordStrengthMeter = ({ password, onStrengthChange }) => {
    const [strength, setStrength] = useState({ score: 0, feedback: [], color: 'gray' });

    const calculateStrength = (password) => {
        if (!password) return { score: 0, feedback: [], color: 'gray' };

        let score = 0;
        const feedback = [];

        // Length check
        if (password.length >= 8) {
            score += 1;
        } else {
            feedback.push('Use at least 8 characters');
        }

        // Uppercase letter
        if (/[A-Z]/.test(password)) {
            score += 1;
        } else {
            feedback.push('Add uppercase letters');
        }

        // Lowercase letter
        if (/[a-z]/.test(password)) {
            score += 1;
        } else {
            feedback.push('Add lowercase letters');
        }

        // Numbers
        if (/\d/.test(password)) {
            score += 1;
        } else {
            feedback.push('Add numbers');
        }

        // Special characters
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            score += 1;
        } else {
            feedback.push('Add special characters (!@#$...)');
        }

        // Common patterns penalty
        const commonPatterns = [
            /123456/,
            /password/i,
            /qwerty/i,
            /abc123/i,
            /(.)\1{2,}/ // Repeated characters
        ];

        if (commonPatterns.some(pattern => pattern.test(password))) {
            score = Math.max(0, score - 1);
            feedback.push('Avoid common patterns');
        }

        // Determine color and label
        let color = 'gray';
        let label = '';

        if (score === 0) {
            color = 'gray';
            label = '';
        } else if (score <= 2) {
            color = 'red';
            label = 'Weak';
        } else if (score <= 3) {
            color = 'yellow';
            label = 'Fair';
        } else if (score <= 4) {
            color = 'blue';
            label = 'Good';
        } else {
            color = 'green';
            label = 'Strong';
        }

        return { score, feedback, color, label };
    };

    useEffect(() => {
        const newStrength = calculateStrength(password);
        setStrength(newStrength);
        onStrengthChange(newStrength);
    }, [password, onStrengthChange]);

    if (!password) return null;

    const getColorClasses = (color) => {
        switch (color) {
            case 'red': return 'bg-red-500 text-red-700';
            case 'yellow': return 'bg-yellow-500 text-yellow-700';
            case 'blue': return 'bg-blue-500 text-blue-700';
            case 'green': return 'bg-green-500 text-green-700';
            default: return 'bg-gray-300 text-gray-600';
        }
    };

    const getProgressWidth = () => {
        return `${(strength.score / 5) * 100}%`;
    };

    return (
        <div className="mt-2 space-y-2">
            {/* Strength Bar */}
            <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                        className={`h-2 rounded-full transition-all duration-300 ${strength.color === 'red' ? 'bg-red-500' :
                                strength.color === 'yellow' ? 'bg-yellow-500' :
                                    strength.color === 'blue' ? 'bg-blue-500' :
                                        strength.color === 'green' ? 'bg-green-500' :
                                            'bg-gray-300'
                            }`}
                        style={{ width: getProgressWidth() }}
                    />
                </div>
                {strength.label && (
                    <span className={`text-xs font-medium ${strength.color === 'red' ? 'text-red-600' :
                            strength.color === 'yellow' ? 'text-yellow-600' :
                                strength.color === 'blue' ? 'text-blue-600' :
                                    strength.color === 'green' ? 'text-green-600' :
                                        'text-gray-600'
                        }`}>
                        {strength.label}
                    </span>
                )}
            </div>

            {/* Feedback */}
            {strength.feedback.length > 0 && (
                <div className="space-y-1">
                    {strength.feedback.map((item, index) => (
                        <div key={index} className="flex items-center text-xs text-accent-600">
                            <Icon name="info" className="w-3 h-3 mr-1" />
                            {item}
                        </div>
                    ))}
                </div>
            )}

            {/* Success message for strong passwords */}
            {strength.score >= 4 && (
                <div className="flex items-center text-xs text-green-600">
                    <Icon name="check-circle" className="w-3 h-3 mr-1" />
                    Great! Your password is strong
                </div>
            )}
        </div>
    );
};

export default PasswordStrengthMeter; 